import React from 'react';
import { isChunkLoadError } from 'helpers/lazyWithRetry';
import { palette, typography } from 'theme/tokens';

/**
 * Last line of defence against a blank document.
 *
 * React 18 unmounts the whole tree when a render error goes unhandled, and with
 * no boundary anywhere in this app the result was a white page with nothing on
 * it — the failure that hid the missing change-password route, and the failure
 * that a stale code-split chunk produced after a deploy. Whatever the cause, an
 * error should leave something on screen that says what happened.
 *
 * Framework-neutral by necessity: this sits above BOTH consoles in `index.js`,
 * and the MUI and AntD providers are mounted below it, so neither library's
 * components are available here. Plain inline styles only.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the component stack: the message alone rarely identifies which screen
    // failed, and there is no other record of this once the tree is gone.
    console.error('Unhandled render error', error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    /**
     * A stale chunk is not a bug the user can act on and not something a support
     * ticket should be raised for — the app is simply older than the server. Say
     * that, rather than showing them a stack trace for a fixed problem.
     */
    const isStaleBuild = isChunkLoadError(error);

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: typography.fontFamily.interSystem,
          background: palette.custom.blue[22],
        }}
      >
        <div style={{ maxWidth: 460, textAlign: 'center' }}>
          <h1 style={{ fontSize: typography.fontSize[20], fontWeight: typography.fontWeight.semiBold, margin: '0 0 8px', color: palette.custom.blue[800] }}>
            {isStaleBuild ? 'A newer version is available' : 'Something went wrong'}
          </h1>
          <p style={{ fontSize: typography.fontSize[14], lineHeight: typography.lineHeight.loose, margin: '0 0 20px', color: palette.custom.blue[440] }}>
            {isStaleBuild
              ? 'This tab was open while the console was updated. Reload to pick up the new version — nothing has been lost.'
              : 'This page could not be displayed. Reloading usually clears it; if it keeps happening, please report it.'}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '10px 22px',
              fontSize: typography.fontSize[14],
              fontWeight: typography.fontWeight.semiBold,
              color: palette.white,
              background: palette.custom.indigo[750],
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          {!isStaleBuild && (
            // Only for genuine faults. Showing "Loading chunk 4264 failed" under
            // the stale-build message would contradict the reassurance above it.
            <pre
              style={{
                marginTop: 20,
                padding: 12,
                fontSize: typography.fontSize[12],
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: palette.custom.blue[440],
                background: palette.white,
                borderRadius: 8,
              }}
            >
              {String(error?.message || error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
